import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { portalRoot } from '../../lib/portalRoot';

// ============================================================================
// Shared popover plumbing for the form-controls standard (DatePicker,
// TimePicker, Combobox). One place for: portal to portalRoot (fullscreen-
// safe), fixed positioning under the trigger with a flip-above when the
// bottom of the viewport is close, reposition on scroll/resize, close on
// outside mousedown and on Escape.
//
// Escape: Modal's document-level handler is registered before ours (the
// modal mounted first), so it would close the whole modal on the first
// Escape. Every open panel carries `data-bbm-popover`; Modal checks for
// that attribute and yields. Keep the attribute if you restyle this.
// ============================================================================

const GAP = 6;

export function usePopoverPosition({ open, anchorRef, panelRef, width, align = 'left' }) {
    const [style, setStyle] = useState(null);

    useLayoutEffect(() => {
        if (!open) { setStyle(null); return; }
        const place = () => {
            const a = anchorRef.current?.getBoundingClientRect();
            if (!a) return;
            const panelH = panelRef.current?.offsetHeight || 0;
            const panelW = width === 'anchor' ? a.width : (typeof width === 'number' ? width : (panelRef.current?.offsetWidth || 0));
            const vw = window.innerWidth, vh = window.innerHeight;
            let left = align === 'right' ? a.right - panelW : a.left;
            left = Math.max(8, Math.min(left, vw - panelW - 8));
            const below = a.bottom + GAP;
            const fitsBelow = below + panelH <= vh - 8;
            const top = fitsBelow || a.top - GAP - panelH < 8 ? below : a.top - GAP - panelH;
            setStyle({ position: 'fixed', top, left, width: width === 'anchor' ? a.width : (typeof width === 'number' ? width : undefined), zIndex: 160 }); // z-map: above secondary modals (150), below lightbox (200)
        };
        place();
        // Second pass once the panel has real dimensions (first pass may
        // run before it has laid out).
        const raf = requestAnimationFrame(place);
        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('scroll', place, true);
            window.removeEventListener('resize', place);
        };
    }, [open, width, align, anchorRef, panelRef]);

    return style;
}

export function useDismiss({ open, onClose, anchorRef, panelRef }) {
    useEffect(() => {
        if (!open) return;
        // Nested popovers (2026-09-18, TripStrip): a panel opened FROM inside
        // this one is a later sibling in the portal root — clicks in it and
        // its Escape belong to it, not to us.
        const laterPanelHolds = (target) => {
            const other = target?.closest?.('[data-bbm-popover]');
            const me = panelRef.current;
            return !!(other && me && other !== me && (me.compareDocumentPosition(other) & Node.DOCUMENT_POSITION_FOLLOWING));
        };
        const onDown = (e) => {
            if (anchorRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
            if (laterPanelHolds(e.target)) return;
            onClose();
        };
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            const me = panelRef.current;
            if (me) {
                const all = [...document.querySelectorAll('[data-bbm-popover]')];
                if (all[all.length - 1] !== me) return; // an inner panel owns this Escape
            }
            e.stopPropagation();
            onClose();
            // Hand focus back to the trigger so a second Escape reaches
            // the modal (or whatever owns the page).
            const a = anchorRef.current;
            if (a && typeof a.focus === 'function') a.focus();
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose, anchorRef, panelRef]);
}

// The floating panel itself. `width`: 'anchor' (match the trigger), a
// number (px), or undefined (content-sized). Renders nothing when closed.
export function PopoverPanel({ open, onClose, anchorRef, width, align, role = 'dialog', ariaLabel, className = '', children }) {
    const panelRef = useRef(null);
    const style = usePopoverPosition({ open, anchorRef, panelRef, width, align });
    useDismiss({ open, onClose, anchorRef, panelRef });
    if (!open) return null;
    return createPortal(
        <div
            ref={panelRef}
            role={role}
            aria-label={ariaLabel}
            data-bbm-popover=""
            style={{ ...(style || { position: 'fixed', top: -9999, left: -9999 }), animation: 'bbm-pop-drop var(--duration-popover, 160ms) var(--ease-out, ease-out)' }}
            className={`bg-white border border-slate-200 rounded-xl shadow-[0_12px_32px_rgba(15,23,42,0.14)] ${className}`}
        >
            <style>{`@keyframes bbm-pop-drop { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }`}</style>
            {children}
        </div>,
        portalRoot()
    );
}

// Trigger button shared by DatePicker / TimePicker: looks like an input,
// shows the current value on the left and a small glyph on the right.
export const TRIGGER_CLS = {
    md: 'h-10 px-3 text-sm',
    sm: 'h-8 px-2.5 text-[13px]',
};
export function TriggerButton({ innerRef, onClick, open, size = 'md', placeholder, empty, glyph, ariaLabel, className = '', disabled, children }) {
    return (
        <button
            ref={innerRef}
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={ariaLabel}
            className={`w-full flex items-center justify-between gap-2 rounded-lg border bg-white text-left transition-colors ${TRIGGER_CLS[size] || TRIGGER_CLS.md} ${
                open ? 'border-red-400 ring-2 ring-red-500/20' : 'border-slate-200 hover:border-slate-300'
            } focus:outline-none focus-visible:border-red-400 focus-visible:ring-2 focus-visible:ring-red-500/20 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed ${className}`}
        >
            <span className={`truncate ${empty ? 'text-slate-400' : 'text-slate-800 font-medium'}`}>{empty ? placeholder : children}</span>
            <span className="text-slate-400 flex-shrink-0" aria-hidden="true">{glyph}</span>
        </button>
    );
}
